import { useTranslation } from 'react-i18next';
import type { ActiveLocation } from '../model/active-location';

/**
 * Where you are: module, then section. It replaces the old header's static `Console` heading, which
 * said the same thing on all nineteen screens and therefore said nothing.
 *
 * It reads the SAME `ActiveLocation` the rail marks itself from, so the breadcrumb and the rail can
 * never disagree about which module you are in — the failure that a second `pathname.startsWith`
 * chain in this file would eventually have produced.
 *
 * Below 900px of shell width the module segment and the separator are hidden by CSS and the section
 * alone remains, because at that width the rail is gone and the breadcrumb is the only thing naming
 * the screen. It is not a link: the module's own rail entry and the bottom bar are the way back,
 * and a one-level-up link in a two-level hierarchy is a control that duplicates navigation already
 * on screen.
 *
 * IT IS THE PAGE'S `<h1>`. The old header carried a literal `<h1>Console</h1>` on all nineteen
 * screens, and every screen's own title is an `<h2>` beneath it — so removing the `h1` outright
 * would leave every page starting at level two. This element takes that role and, unlike `Console`,
 * actually names the screen. Its 13px size is chrome styling, not a statement about its level.
 *
 * The screen's own `.screen-title` still names the content, and on an index screen the two read the
 * same word. That is location versus heading, not the guidelines' "nothing named twice": on a
 * detail they diverge — `Studi clinici / Studi` above `ACME-01` — and the breadcrumb is the only
 * thing on the page that says which module `ACME-01` belongs to.
 */
export function ShellBreadcrumb({ active }: { active: ActiveLocation }) {
  const { t } = useTranslation('shell');

  const moduleLabel = active.module?.label;
  const leafLabel =
    active.section?.label ?? active.hiddenSection?.label ?? active.auxiliary?.label ?? moduleLabel;

  // A URL matching no module and no auxiliary screen — an unrouted path reaching the shell's own
  // error boundary, or a module added to the router but not to the catalog. Naming the product is
  // the honest fallback; going blank reads as a broken header.
  if (leafLabel === undefined) {
    return <h1 className="shell-crumb shell-crumb-section">{t('nav.productName')}</h1>;
  }

  const showModuleSegment = moduleLabel !== undefined && moduleLabel !== leafLabel;

  return (
    <h1 className="shell-crumb">
      {showModuleSegment ? (
        <>
          <span className="shell-crumb-module">{moduleLabel}</span>
          <span className="shell-crumb-sep" aria-hidden="true">
            /
          </span>
        </>
      ) : null}
      <span className="shell-crumb-section">{leafLabel}</span>
    </h1>
  );
}
